import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { executeQuery, executeMutation } from "../../../graphql/client";

// Compete board for one event (pickleball mini-tourneys etc.) —
// honor-system game logging on top of the native RSVP:
//   1. RSVP yes to the event (dashboard or the button here).
//   2. Log every game you play: win/loss, who with, who against,
//      score. Randoms from the park are fine as text — they just
//      never earn board credit because they never RSVP'd.
//   3. Games stay "pending" until a captain or admin approves your
//      RSVP; once approved they count automatically unless disputed
//      (by an admin or any approved player at the event). Clearing a
//      dispute or removing someone else's game is admin-only (#291).
// Board scoring is phrased in wins on purpose: a counted win = 3
// wins on the board, and any counted game = at least 1 (a loss
// still pays 1 for playing). Board wins roll into the long-running
// wins leaderboard.

const BOARD_FIELDS = `
  socialEventId
  title
  date
  myRsvpStatus
  canLogGames
  canApprove
  canModerate
  players {
    userSub
    name
    approved
    gamesLogged
    gameWins
    gameLosses
    boardWins
  }
  games {
    gameId
    loggedBy
    loggedByName
    result
    partners
    opponents
    score
    createdAt
    status
    boardWins
    disputedByName
    disputeNote
    isMine
    canDispute
  }
`;

const QUERY = `
  query EventCompete($socialEventId: ID!) {
    eventCompete(socialEventId: $socialEventId) { ${BOARD_FIELDS} }
  }
`;

const card = {
  background: "#fff",
  border: "1px solid #e3e7ef",
  borderRadius: 10,
  padding: 16,
  marginBottom: 16,
};

const chipStyle = (bg, fg) => ({
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  background: bg,
  color: fg,
});

const STATUS_CHIP = {
  counted: { bg: "#e4f5e9", fg: "#1f7a3a", label: "✅ counts" },
  pending: { bg: "#fff6de", fg: "#8a5a00", label: "⏳ pending approval" },
  disputed: { bg: "#fdeaea", fg: "#b02a2a", label: "⚠️ disputed" },
};

const inputStyle = {
  width: "100%",
  padding: "9px 10px",
  border: "1px solid #d0d6e0",
  borderRadius: 6,
  fontSize: 14,
  boxSizing: "border-box",
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const Compete = () => {
  const { socialEventId } = useParams();
  const navigate = useNavigate();

  const [board, setBoard] = useState(null);
  // Season this night belongs to, if any (epic #307 phase 2) —
  // best-effort; the board never waits on it.
  const [season, setSeason] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  // Log-game form
  const [result, setResult] = useState("win");
  const [partners, setPartners] = useState("");
  const [opponents, setOpponents] = useState("");
  const [score, setScore] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await executeQuery(QUERY, { socialEventId });
      setBoard(data.eventCompete);
      setLoadError("");
    } catch (err) {
      setLoadError(err.message || "Could not load the board");
    } finally {
      setLoading(false);
    }
  }, [socialEventId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    executeQuery(`query { seasons { seasonId name eventIds } }`)
      .then((data) => {
        if (cancelled) return;
        const hit = (data.seasons || []).find((s) =>
          (s.eventIds || []).includes(socialEventId)
        );
        setSeason(hit || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [socialEventId]);

  // Every mutation returns the refreshed board, so one helper covers
  // them all: run it, swap the board in, surface any error.
  const runBoardMutation = async (mutation, variables, resultKey) => {
    setBusy(true);
    setActionError("");
    try {
      const data = await executeMutation(mutation, variables);
      setBoard(data[resultKey]);
    } catch (err) {
      setActionError(err.message || "That didn't save — try again.");
    } finally {
      setBusy(false);
    }
  };

  const rsvpYes = async () => {
    setBusy(true);
    setActionError("");
    try {
      await executeMutation(
        `mutation RsvpYes($socialEventId: ID!) {
          rsvpToEvent(socialEventId: $socialEventId, status: "yes") { myRsvp }
        }`,
        { socialEventId }
      );
      await load();
    } catch (err) {
      setActionError(err.message || "RSVP failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  const logGame = async (e) => {
    e.preventDefault();
    await runBoardMutation(
      `mutation LogGame($socialEventId: ID!, $result: String!, $partners: String, $opponents: String, $score: String) {
        logEventGame(socialEventId: $socialEventId, result: $result, partners: $partners, opponents: $opponents, score: $score) { ${BOARD_FIELDS} }
      }`,
      { socialEventId, result, partners, opponents, score },
      "logEventGame"
    );
    setPartners("");
    setOpponents("");
    setScore("");
  };

  const disputeGame = (gameId, undo) => {
    let note = null;
    if (!undo) {
      // eslint-disable-next-line no-alert
      note = window.prompt(
        "What's wrong with this game? (optional note)",
        ""
      );
      if (note === null) return; // cancelled
    }
    runBoardMutation(
      `mutation DisputeGame($socialEventId: ID!, $gameId: ID!, $note: String, $undo: Boolean) {
        disputeEventGame(socialEventId: $socialEventId, gameId: $gameId, note: $note, undo: $undo) { ${BOARD_FIELDS} }
      }`,
      { socialEventId, gameId, note: note || null, undo: !!undo },
      "disputeEventGame"
    );
  };

  const deleteGame = (gameId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remove this game from the log?")) return;
    runBoardMutation(
      `mutation DeleteGame($socialEventId: ID!, $gameId: ID!) {
        deleteEventGame(socialEventId: $socialEventId, gameId: $gameId) { ${BOARD_FIELDS} }
      }`,
      { socialEventId, gameId },
      "deleteEventGame"
    );
  };

  const approvePlayer = (userSub, approved) => {
    runBoardMutation(
      `mutation ApproveRsvp($socialEventId: ID!, $userSub: String!, $approved: Boolean!) {
        approveEventRsvp(socialEventId: $socialEventId, userSub: $userSub, approved: $approved) { ${BOARD_FIELDS} }
      }`,
      { socialEventId, userSub, approved },
      "approveEventRsvp"
    );
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto", padding: 16 }}>
        <p style={{ color: "#5b667a" }}>Loading the board…</p>
      </div>
    );
  }

  if (loadError || !board) {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto", padding: 16 }}>
        <div style={card}>
          <p style={{ color: "#b02a2a", marginBottom: 12 }}>
            {loadError || "Could not load the board"}
          </p>
          <p style={{ color: "#5b667a", fontSize: 14 }}>
            You need to be logged in as a member to see the compete
            board.
          </p>
          <button
            type="button"
            onClick={() => navigate("/login")}
            style={{
              padding: "9px 18px",
              background: "#1f7a3a",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Log in
          </button>
        </div>
      </div>
    );
  }

  const myStatus = board.myRsvpStatus;

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: 16 }}>
      <button
        type="button"
        onClick={() => navigate("/dashboard")}
        style={{
          background: "none",
          border: "none",
          color: "#2f6fb3",
          fontSize: 14,
          cursor: "pointer",
          padding: 0,
          marginBottom: 10,
        }}
      >
        ← Back to dashboard
      </button>

      <h1 style={{ fontSize: 24, marginBottom: 2 }}>
        🏆 {board.title || board.socialEventId}
      </h1>
      <div style={{ color: "#5b667a", fontSize: 14, marginBottom: 14 }}>
        {board.date || ""} · honor-system game log · win = 3 wins on the
        board, every game you play = at least 1
      </div>
      {season && (
        <button
          type="button"
          onClick={() => navigate(`/seasons/${season.seasonId}`)}
          style={{
            ...chipStyle("#e7f4f3", "#0e7c9b"),
            border: "none",
            cursor: "pointer",
            marginBottom: 14,
          }}
        >
          <span role="img" aria-label="stadium">🏟️</span> Part of {season.name}{" "}
          · season standings →
        </button>
      )}

      {/* My status / call to action */}
      <div style={card}>
        {myStatus === "none" && (
          <>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              RSVP to start logging games
            </div>
            <p style={{ fontSize: 14, color: "#5b667a", marginBottom: 10 }}>
              RSVP yes and you can log games right away. Your games count
              on the board once your RSVP is approved.
            </p>
            <button
              type="button"
              onClick={rsvpYes}
              disabled={busy}
              style={{
                padding: "10px 18px",
                background: "#1f7a3a",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontWeight: 700,
                cursor: busy ? "default" : "pointer",
              }}
            >
              ✅ RSVP yes to this event
            </button>
          </>
        )}
        {myStatus === "pending" && (
          <div style={{ fontSize: 14, color: "#8a5a00" }}>
            <strong>⏳ You're in — approval pending.</strong> Log your
            games now; they'll count automatically once your RSVP is
            approved.
          </div>
        )}
        {myStatus === "approved" && (
          <div style={{ fontSize: 14, color: "#1f7a3a" }}>
            <strong>✅ You're approved.</strong> Games you log count on
            the board right away (unless disputed).
          </div>
        )}
      </div>

      {/* Log a game */}
      {board.canLogGames && (
        <form style={card} onSubmit={logGame}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>
            Log a game
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {[
              { key: "win", label: "🏅 We won" },
              { key: "loss", label: "➖ We lost" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setResult(opt.key)}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  background: result === opt.key ? "#1f3a5f" : "#fff",
                  color: result === opt.key ? "#fff" : "#3a4150",
                  border:
                    result === opt.key
                      ? "1px solid #1f3a5f"
                      : "1px solid #d0d6e0",
                  borderRadius: 6,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            <input
              style={inputStyle}
              placeholder="Who was on your team? (e.g. me + Sarah)"
              value={partners}
              maxLength={300}
              onChange={(e) => setPartners(e.target.value)}
            />
            <input
              style={inputStyle}
              placeholder="Who did you play against? (randoms from the park are fine)"
              value={opponents}
              maxLength={300}
              onChange={(e) => setOpponents(e.target.value)}
            />
            <input
              style={inputStyle}
              placeholder="Score (optional, e.g. 11-7)"
              value={score}
              maxLength={60}
              onChange={(e) => setScore(e.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            style={{
              marginTop: 10,
              padding: "10px 18px",
              background: "#1f7a3a",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontWeight: 700,
              cursor: busy ? "default" : "pointer",
            }}
          >
            {busy ? "Saving…" : "Log this game"}
          </button>
          <div style={{ marginTop: 8, fontSize: 12, color: "#8b94a6" }}>
            Each player logs their own games — if your partner is in the
            group, they log it too and get their own board wins.
          </div>
        </form>
      )}

      {actionError && (
        <div
          style={{
            ...card,
            borderColor: "#f3c2c2",
            background: "#fdf3f3",
            color: "#b02a2a",
            fontSize: 14,
          }}
        >
          {actionError}
        </div>
      )}

      {/* Standings */}
      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>
          Standings — this event
        </div>
        {board.players.length === 0 ? (
          <div style={{ fontSize: 14, color: "#5b667a" }}>
            Nobody has RSVP'd yes yet.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    textAlign: "left",
                    fontSize: 12,
                    color: "#5b667a",
                  }}
                >
                  <th style={{ padding: "6px 8px" }}>Player</th>
                  <th style={{ padding: "6px 8px" }}>Games</th>
                  <th style={{ padding: "6px 8px" }}>W-L</th>
                  <th style={{ padding: "6px 8px" }}>Board wins</th>
                  <th style={{ padding: "6px 8px" }} />
                </tr>
              </thead>
              <tbody>
                {board.players.map((p) => (
                  <tr
                    key={p.userSub}
                    style={{ borderTop: "1px solid #eef1f6", fontSize: 14 }}
                  >
                    <td style={{ padding: "8px" }}>
                      {p.name || "Member"}{" "}
                      {p.approved ? (
                        <span style={chipStyle("#e4f5e9", "#1f7a3a")}>
                          approved
                        </span>
                      ) : (
                        <span style={chipStyle("#fff6de", "#8a5a00")}>
                          pending
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "8px" }}>{p.gamesLogged}</td>
                    <td style={{ padding: "8px" }}>
                      {p.gameWins}-{p.gameLosses}
                    </td>
                    <td style={{ padding: "8px", fontWeight: 700 }}>
                      {p.boardWins}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {board.canApprove && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            approvePlayer(p.userSub, !p.approved)
                          }
                          style={{
                            padding: "5px 10px",
                            background: p.approved ? "#fff" : "#1f7a3a",
                            color: p.approved ? "#b02a2a" : "#fff",
                            border: p.approved
                              ? "1px solid #e0b4b4"
                              : "none",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {p.approved ? "Revoke" : "Approve"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 8, fontSize: 12, color: "#8b94a6" }}>
          Board wins count only approved players' games: 3 per win, 1 per
          loss. They roll into the group's long-running wins leaderboard.
        </div>
      </div>

      {/* Game log */}
      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>
          Game log ({board.games.length})
        </div>
        {board.games.length === 0 ? (
          <div style={{ fontSize: 14, color: "#5b667a" }}>
            No games logged yet — be the first.
          </div>
        ) : (
          board.games.map((g) => {
            const chip = STATUS_CHIP[g.status] || STATUS_CHIP.pending;
            return (
              <div
                key={g.gameId}
                style={{
                  borderTop: "1px solid #eef1f6",
                  padding: "10px 0",
                  fontSize: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <strong>
                      {g.result === "win" ? "🏅 Win" : "➖ Loss"}
                    </strong>{" "}
                    — {g.loggedByName || "Member"}
                    {g.partners ? ` (team: ${g.partners})` : ""}
                    {g.opponents ? ` vs ${g.opponents}` : ""}
                    {g.score ? ` · ${g.score}` : ""}
                  </div>
                  <div>
                    <span style={chipStyle(chip.bg, chip.fg)}>
                      {chip.label}
                      {g.status === "counted" && ` +${g.boardWins}`}
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: "#8b94a6",
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <span>{fmtWhen(g.createdAt)}</span>
                  {g.status === "disputed" && (
                    <span style={{ color: "#b02a2a" }}>
                      disputed by {g.disputedByName || "admin"}
                      {g.disputeNote ? `: ${g.disputeNote}` : ""}
                    </span>
                  )}
                  {g.canDispute && g.status !== "disputed" && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => disputeGame(g.gameId, false)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#b02a2a",
                        fontSize: 12,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      dispute
                    </button>
                  )}
                  {board.canModerate && g.status === "disputed" && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => disputeGame(g.gameId, true)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#1f7a3a",
                        fontSize: 12,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      clear dispute
                    </button>
                  )}
                  {(g.isMine || board.canModerate) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => deleteGame(g.gameId)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#8b94a6",
                        fontSize: 12,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      remove
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Compete;
